import type { MeResponse } from '@/services/apis/auth/auth.schemas';
import { createSliceWithImmer } from 'zustand-slices/immer';

export type AuthFlowStep =
  | 'email'
  | 'postcode'
  | 'customerId'
  | 'verifyAccount'
  | 'confirmOTP';

export type VerifyAccountMethod = 'EMAIL' | 'SMS';

export type AuthFlow = {
  email: string;
  currentStep: AuthFlowStep;
};

type AuthSlice = {
  initialAccessToken: string;
  loginAccessToken: string;
  isAuthenticated: boolean;
  authFlow: AuthFlow;
  isAuthSheetOpen: boolean;
  loginCredentials: MeResponse | null;
  agentName: string | null;
  isAuthSheetLoading: boolean;
  customerId: string | null;
};

type AuthSliceActions = {
  setIsAuthenticated: (isAuthenticated: boolean) => (state: AuthSlice) => void;
  setAuthFlowEmail: (email: string) => (state: AuthSlice) => void;
  setAuthFlowCurrentStep: (
    currentStep: AuthFlowStep
  ) => (state: AuthSlice) => void;
  setLogout: () => (state: AuthSlice) => void;
  setIsAuthSheetOpen: (isOpen: boolean) => (state: AuthSlice) => void;
  setInitialAccessToken: (accessToken: string) => (state: AuthSlice) => void;
  setLoginAccessToken: (accessToken: string) => (state: AuthSlice) => void;
  setLoginCredentials: (data: MeResponse) => (state: AuthSlice) => void;
  setAgentName: (name: string) => (state: AuthSlice) => void;
  setAgentLogout: () => (state: AuthSlice) => void;
  setIsAuthSheetLoading: (
    isAuthSheetLoading: boolean
  ) => (state: AuthSlice) => void;
  setCustomerId: (customerId: string) => (state: AuthSlice) => void;
};

export const authSlice = createSliceWithImmer<
  'authStore',
  AuthSlice,
  AuthSliceActions
>({
  name: 'authStore',
  value: {
    isAuthSheetOpen: false,
    isAuthenticated: false,
    customerId: null,
    initialAccessToken: '',
    loginAccessToken: '',
    agentName: null,
    isAuthSheetLoading: false,
    loginCredentials: null,
    authFlow: {
      email: '',
      currentStep: 'email',
    },
  },
  actions: {
    setIsAuthenticated: (isAuthenticated) => (state) => {
      state.isAuthenticated = isAuthenticated;
    },
    setAuthFlowEmail: (email) => (state) => {
      state.authFlow.email = email;
    },
    setAuthFlowCurrentStep: (currentStep) => (state) => {
      state.authFlow.currentStep = currentStep;
    },
    setLogout: () => (state) => {
      state.isAuthenticated = false;
      state.initialAccessToken = '';
      state.isAuthSheetLoading = false;
      state.loginAccessToken = '';
      state.loginCredentials = null;
      state.authFlow = {
        email: '',
        currentStep: 'email',
      };
      state.customerId = null;
    },
    setIsAuthSheetOpen: (isOpen) => (state) => {
      state.isAuthSheetOpen = isOpen;
    },
    setInitialAccessToken: (accessToken) => (state) => {
      state.initialAccessToken = accessToken;
    },
    setLoginAccessToken: (accessToken) => (state) => {
      state.loginAccessToken = accessToken;
    },
    setLoginCredentials: (data) => (state) => {
      state.loginCredentials = data;
      state.isAuthenticated = true;
    },
    setAgentName: (name) => (state) => {
      state.agentName = name;
    },
    setAgentLogout: () => (state) => {
      state.agentName = null;
    },
    setIsAuthSheetLoading: (isAuthSheetLoading) => (state) => {
      state.isAuthSheetLoading = isAuthSheetLoading;
    },
    setCustomerId: (customerId) => (state) => {
      state.customerId = customerId;
    },
  },
});
